import { CollectibleType } from '../collectibles/CollectibleType';

export interface CollectiblesConfig {
  /** Bottle value each collectible type adds to the bottle counter. */
  readonly bottleValue: Readonly<Record<CollectibleType, number>>;
  /** Visual spin speed of collectibles (degrees per second). */
  readonly spinDegreesPerSecond: number;
  /** Visual bob amplitude (meters). */
  readonly bobAmplitude: number;
  readonly bobFrequency: number;
  /**
   * Magnet power-up: pulls all bottles within `radius` m (centre distance runner ↔ pickup) for `duration` s.
   * Characters can have a permanent magnet on top (CharacterPerks.magnetRadius), limited per bottle row:
   * collectibles of one lane less than `rowGap` m apart (along the track) form a row – rows are spaced
   * ≤ 2 m, obstacles and lane switches of a line leave gaps of 4 m and more.
   */
  readonly magnet: { readonly radius: number; readonly duration: number; readonly rowGap: number };
  /** Bottles pulled in by a magnet fly to the runner (visual only – they count on the spot). */
  readonly magnetFlight: {
    readonly seconds: number;
    /** Height above the runner's feet the bottle flies to (m). */
    readonly targetHeight: number;
    /** Scale at arrival – the bottle shrinks into the runner. */
    readonly endScale: number;
  };
  readonly timeBonus: { readonly seconds: number };
  /**
   * Turbo of the beer crate (user 2026-10-03, "wie bei Supersonic"): invulnerable, blinking and `speedFactor` × the
   * run speed for `seconds`; afterwards `graceSeconds` of the usual invulnerability so the end of the turbo never
   * drops the runner into an obstacle. Re-pickup restarts the duration. The speed rises to `speedFactor` within
   * `rampSeconds` and falls back within the last `rampSeconds` (no jolt at 2.5 ×; 0 or unset = at once).
   */
  readonly turbo: { readonly seconds: number; readonly speedFactor: number; readonly graceSeconds: number; readonly rampSeconds?: number };
}

export const COLLECTIBLES_CONFIG: CollectiblesConfig = {
  bottleValue: {
    [CollectibleType.BeerBottle]: 1,
    [CollectibleType.BeerCrate]: 5,
    [CollectibleType.GoldenBottle]: 10,
    [CollectibleType.Toolbox]: 10,
    [CollectibleType.ExtraLife]: 0,
    [CollectibleType.Magnet]: 0,
    [CollectibleType.TimeBonus]: 0,
  },
  spinDegreesPerSecond: 140,
  bobAmplitude: 0.08,
  bobFrequency: 1.6,
  magnet: { radius: 4, duration: 8, rowGap: 3 },
  magnetFlight: { seconds: 0.22, targetHeight: 1, endScale: 0.35 },
  timeBonus: { seconds: 10 },
  // user 2026-10-03: "circa 2,5-fache Geschwindigkeit"
  turbo: { seconds: 6, speedFactor: 2.5, graceSeconds: 1, rampSeconds: 0.5 },
};
